import { getGridDisplayValue } from './AntGridDisplayValue';
import { isInlineDraftRow } from './AntGridInlineEdit';

const CACHE_KEY = 'ante-grid-widths-v1';
const CACHE_TTL = 14 * 24 * 60 * 60 * 1000;
const MAX_SCOPES = 40;
const MAX_COLUMNS = 200;

const columnKey = (column) => JSON.stringify([
    column.dataField, column.caption, column.colType, column.cellTemplate,
    column.displayField, column._antBestFit
]);
const validSize = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 4096;
const validContentWidth = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const eligible = (column) => column.visible !== false && !column.isSystemCol && Boolean(column._antBestFit);
const flexible = (column) => column.visible !== false && !column.isSystemCol && Boolean(column._antFlexibleWidth);
const fitStates = new WeakMap();
// Mức sàn khi NGƯỜI DÙNG kéo độ rộng (minWidth đưa cho DevExtreme). Mức tối thiểu để lưới TỰ chia chỗ
// (độ rộng tiêu đề, nội dung số/ngày, chữ danh mục...) giữ riêng ở column._antLayoutMinWidth — không được chặn
// người dùng thu hẹp cột (cột xuống dòng được, chữ dài thì cắt "...").
export const GRID_RESIZE_FLOOR = 48;
export const toResizeMinWidth = (minWidth, width) => {
    const floor = typeof width === 'number' && width > 0 ? Math.min(GRID_RESIZE_FLOOR, width) : GRID_RESIZE_FLOOR;
    return typeof minWidth === 'number' ? Math.min(minWidth, floor) : floor;
};
// Cột "..." (adaptive) của DevExtreme xuất hiện khi có cột bị ẩn.
const ADAPTIVE_COMMAND_WIDTH = 40;

// DevExtreme ẩn cột (màn hẹp) lần lượt theo hidingPriority thấp nhất và KHÔNG xét lại: cột đã ẩn không hiện trở lại dù
// ẩn tiếp cột sau đó làm dư chỗ. Bố cục tự chia chỗ quyết định cột nào ẩn (có bù lại), rồi tạm bỏ hidingPriority của
// cột được bù để DevExtreme ẩn đúng các cột đã chọn. Mức ưu tiên gốc giữ ở đây (theo lưới) để trả lại khi đủ chỗ.
const priorityOverrides = new WeakMap();
const columnId = (column) => column.name || column.dataField;
const naturalHidingPriority = (instance, column, option) => {
    // Cột đang sắp xếp không bao giờ tự ẩn (keepSortedColumnsShown).
    if (instance && option(column, 'sortOrder')) return undefined;
    const saved = instance ? priorityOverrides.get(instance) : null;
    const key = columnId(column);
    return saved && saved.has(key) ? saved.get(key) : option(column, 'hidingPriority');
};

// Độ rộng nội dung đúng như DevExtreme đo (computed width trừ viền/padding), làm tròn XUỐNG.
// Zoom trình duyệt khác 100% làm viền 1px thành 1,25px...: clientWidth làm tròn lên cao hơn
// chỗ thật vài phần pixel, tổng cột chia theo clientWidth bị DevExtreme coi là tràn và ẩn
// cột ưu tiên thấp nhất (vd "Nội dung"), phần chỗ của nó thành khoảng trắng bên phải.
export const getGridContentWidth = (element) => {
    if (!element) return 0;
    const clientWidth = element.clientWidth || 0;
    const view = element.ownerDocument?.defaultView;
    if (!view?.getComputedStyle) return clientWidth;
    const style = view.getComputedStyle(element);
    let width = parseFloat(style.width);
    if (!Number.isFinite(width) || width <= 0) return clientWidth;
    if (style.boxSizing === 'border-box') {
        width -= (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0)
            + (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    }
    return Math.max(0, Math.min(clientWidth || Infinity, Math.floor(width + 1e-6)));
};

export const getGridViewportWidth = (element) =>
    element?.querySelector('.dx-datagrid-rowsview .dx-scrollable-container')?.clientWidth || element?.clientWidth || 0;

// Allocate from intrinsic minimums on every pass (never previous widths), so a
// wider viewport can bring adaptive-hidden columns back. Surplus goes only to
// columns that stay visible after the simulated adaptive hiding.
export const calculateGridFlexibleWidths = (columns, sizes = new Map(), { width = 0, instance } = {}) => {
    const result = new Map(sizes);
    if (!(width > 0) || !columns.some((column) => flexible(column) || eligible(column))) return result;
    const option = (column, name) => instance?.columnOption(column.name || column.dataField, name) ?? column[name];
    const active = columns.filter((column) => {
        const groupIndex = option(column, 'groupIndex');
        return column.visible !== false && option(column, 'visible') !== false &&
            !(typeof groupIndex === 'number' && groupIndex >= 0 && option(column, 'showWhenGrouped') !== true);
    });
    const pixels = (value) => typeof value === 'string' && value.endsWith('%')
        ? width * (parseFloat(value) || 0) / 100 : parseFloat(value) || 0;
    // Recompute from intrinsic dimensions, never the previous viewport's widths.
    const managed = active.filter((column) => eligible(column) || flexible(column)).map((column) => {
        const size = result.get(column) || column._antBestFit || column;
        // Cột auto người dùng đã kéo độ rộng: độ rộng đó là mức tối thiểu, cột vẫn nhận phần còn thừa (thay cho mức
        // tự chia chỗ — người dùng đã chọn hẹp hơn thì tôn trọng).
        const userMinimum = typeof column._antUserMinWidth === 'number' ? column._antUserMinWidth : null;
        const layoutMinimum = size === column && column._antLayoutMinWidth !== undefined ? column._antLayoutMinWidth : size.minWidth;
        const minimum = Math.ceil(Math.max(0, userMinimum !== null ? userMinimum : pixels(layoutMinimum)));
        const preferred = Math.max(minimum, pixels(size.contentWidth ?? size.width));
        return { column, minimum, preferred, current: minimum };
    });
    let reserved = active.filter((column) => !eligible(column) && !flexible(column)).reduce((sum, column) =>
        sum + Math.max(pixels(option(column, 'minWidth')), pixels(option(column, 'width'))), 0);
    reserved += managed.reduce((sum, item) => sum + item.minimum, 0);
    // Native selection/group commands are not in AntGrid's column definitions.
    // Adaptive-more needs space only when something actually overflows; letting
    // DevExtreme reserve it avoids hiding a column at an exact-fit boundary.
    (instance?.getVisibleColumns?.() || []).forEach((column) => {
        if (column.command && column.command !== 'adaptive' && !columns.some((item) => item.name === column.name && item.name)) {
            reserved += Math.max(pixels(column.minWidth), pixels(column.width) || pixels(column.visibleWidth));
        }
    });

    // Mô phỏng adaptive của DevExtreme trên độ rộng tối thiểu: ẩn dần cột hidingPriority thấp
    // nhất tới khi vừa. Phần dư chỉ chia cho cột SẼ CÒN HIỆN — trước đây cột co giãn bị ẩn
    // (vd "Nội dung") vẫn giữ phần dư, thành khoảng trống bên phải còn "Sản phẩm" không nới.
    // Cột bị ẩn giữ độ rộng tối thiểu nên khung rộng ra thì được tính lại và hiện trở lại.
    const hidden = new Set();
    if (reserved > width) {
        const managedByColumn = new Map(managed.map((item) => [item.column, item]));
        const widthOf = (column) => managedByColumn.has(column)
            ? managedByColumn.get(column).minimum
            : Math.max(pixels(option(column, 'minWidth')), pixels(option(column, 'width')));
        const priorityOf = (column) => naturalHidingPriority(instance, column, option);
        const hideable = active
            .filter((column) => !column.isSystemCol && typeof priorityOf(column) === 'number')
            .sort((a, b) => priorityOf(a) - priorityOf(b));
        let total = reserved + ADAPTIVE_COMMAND_WIDTH;
        for (let i = 0; i < hideable.length && total > width; i++) {
            // DevExtreme luôn giữ lại ít nhất 1 cột nội dung.
            if (active.filter((column) => !column.isSystemCol && !hidden.has(column)).length <= 1) break;
            hidden.add(hideable[i]);
            total -= widthOf(hideable[i]);
        }
        // Bù lại: cột đã ẩn (quan trọng hơn trước) vừa phần chỗ còn dư thì hiện trở lại.
        Array.from(hidden)
            .reverse()
            .forEach((column) => {
                const need = widthOf(column);
                if (total + need <= width) {
                    hidden.delete(column);
                    total += need;
                }
            });
        if (!hidden.size) total -= ADAPTIVE_COMMAND_WIDTH;
        reserved = total;
    }
    result.adaptiveHidden = hidden;
    const shown = managed.filter((item) => !hidden.has(item.column));

    let fluid = shown.filter(({ column }) => flexible(column));
    // Grouping, the column chooser or adaptive hiding can remove the original receiver.
    if (!fluid.length && shown.length) {
        const freeText = shown.filter(({ column }) => ['primaryText', 'name', 'longText', 'other'].includes(column._antBestFit?.role));
        fluid = [(freeText.length ? freeText : shown).reduce((a, b) => a.preferred >= b.preferred ? a : b)];
    }
    const receivers = new Set(fluid);
    let remaining = Math.max(0, Math.floor(width - reserved));
    const satisfyContent = (items) => {
        const growing = items.filter((item) => item.preferred > item.current)
            .sort((a, b) => (a.preferred - a.current) - (b.preferred - b.current));
        growing.forEach((item, index) => {
            const extra = Math.min(Math.ceil(item.preferred - item.current), Math.floor(remaining / (growing.length - index)));
            item.current += extra;
            remaining -= extra;
        });
    };
    // Keep lookup/scalar columns at their natural size before stretching text.
    // Each auto column also gets its content demand considered before surplus
    // is shared: equal division alone clips long text beside a mostly empty cell.
    satisfyContent(shown.filter((item) => !receivers.has(item)));
    satisfyContent(fluid);
    fluid.forEach((item, index) => {
        const extra = Math.floor(remaining / (fluid.length - index));
        item.current += extra;
        remaining -= extra;
    });
    managed.forEach(({ column, current, minimum }) => {
        result.set(column, { ...result.get(column), width: current, minWidth: minimum });
    });
    return result;
};

const pendingDimensions = new WeakSet();
const scheduleGridDimensions = (instance) => {
    if (!instance?.updateDimensions || pendingDimensions.has(instance)) return;
    pendingDimensions.add(instance);
    let disposed = false;
    const onDisposing = () => { disposed = true; };
    instance.on?.('disposing', onDisposing);
    // Width option changes can leave DevExtreme's adaptiveHidden state stale.
    // Reconcile once after the whole transaction, before the browser paints.
    Promise.resolve().then(() => {
        pendingDimensions.delete(instance);
        if (disposed) return;
        instance.off?.('disposing', onDisposing);
        const root = instance.element();
        const element = root?.get ? root.get(0) : root;
        if (element?.isConnected) {
            const state = fitStates.get(instance);
            if (state) state.reconciling = true;
            try { instance.updateDimensions(); }
            finally { if (state) state.reconciling = false; }
        }
    });
};

// Only dimensions are persisted; visibility and row values never enter storage.
// A failed/quota-limited localStorage must not interrupt a data load.
export const createGridWidthCache = (scope, storage) => {
    const getStorage = () => storage === undefined ? window.localStorage : storage;
    const readBucket = () => {
        try {
            const parsed = JSON.parse(getStorage().getItem(CACHE_KEY));
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (_) { return {}; }
    };
    let entry = readBucket()[scope];
    if (!entry || Date.now() - entry.at > CACHE_TTL || !entry.columns || typeof entry.columns !== 'object' || Array.isArray(entry.columns) || !Number.isFinite(entry.at))
        entry = { columns: {} };
    return {
        get(column) {
            if (!eligible(column)) return undefined;
            const size = entry.columns[columnKey(column)];
            if (!size || !validSize(size.width) || !validSize(size.minWidth)) return undefined;
            return { width: size.width, minWidth: size.minWidth,
                ...(validContentWidth(size.contentWidth) ? { contentWidth: size.contentWidth } : {}) };
        },
        save(columns, sizes) {
            let changed = false;
            columns.filter(eligible).forEach((column) => {
                const size = sizes.get(column);
                if (!size || !validSize(size.width) || !validSize(size.minWidth)) return;
                const key = columnKey(column);
                const old = entry.columns[key];
                const contentWidth = validContentWidth(size.contentWidth) ? size.contentWidth : undefined;
                if (old?.width === size.width && old?.minWidth === size.minWidth && old?.contentWidth === contentWidth) return;
                entry.columns[key] = { width: size.width, minWidth: size.minWidth,
                    ...(contentWidth !== undefined ? { contentWidth } : {}) };
                changed = true;
            });
            if (!changed) return;
            entry.at = Date.now();
            entry.columns = Object.fromEntries(Object.entries(entry.columns).slice(-MAX_COLUMNS));
            try {
                const bucket = readBucket();
                bucket[scope] = entry;
                const recent = Object.entries(bucket)
                    .filter(([, value]) => value && Number.isFinite(value.at) && Date.now() - value.at <= CACHE_TTL)
                    .sort((a, b) => b[1].at - a[1].at).slice(0, MAX_SCOPES);
                getStorage().setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(recent)));
            } catch (_) { /* Keep the in-memory fallback when storage is unavailable. */ }
        }
    };
};

// Read font/padding once per batch, never measure or resize individual grid cells.
export const createGridTextMeasurer = (element) => {
    const doc = element?.ownerDocument || (typeof document !== 'undefined' ? document : null);
    let context;
    try { context = doc?.createElement('canvas').getContext('2d'); } catch (_) { /* SSR/jsdom */ }
    const styleOf = (selector, fallbackSelector) => {
        const target = element?.querySelector(selector) || element?.querySelector(fallbackSelector) || element?.querySelector('.dx-datagrid') || element;
        if (!target || !doc?.defaultView) return null;
        const style = doc.defaultView.getComputedStyle(target);
        // A CSSStyleDeclaration may be live. Freeze the measurement inputs so
        // our own adaptive DOM changes cannot change the next measurement.
        return Object.fromEntries(['fontSize', 'fontFamily', 'fontWeight', 'letterSpacing', 'paddingLeft', 'paddingRight']
            .map((name) => [name, style[name]]));
    };
    const cell = styleOf('.dx-data-row > td.ant-grid-cell-smart', '.dx-datagrid-rowsview .dx-datagrid-table');
    const header = styleOf('.dx-header-row .dx-datagrid-text-content', '.dx-datagrid-headers .dx-datagrid-table');
    const headerCell = styleOf('.dx-header-row > td[aria-colindex]', '.dx-header-row > td');
    const hasHeaderFilter = Boolean(element?.querySelector('.dx-header-row .dx-header-filter'));
    const gutter = (style) => Math.max(18, (parseFloat(style?.paddingLeft) || 0) + (parseFloat(style?.paddingRight) || 0) + 4);
    const styles = { cell, header };
    const measured = new Map();
    return (text, kind = 'cell', bold = false, column = {}) => {
        text = String(text);
        const style = styles[kind];
        const fontSize = parseFloat(style?.fontSize) || 14;
        const font = `${bold ? '700' : style?.fontWeight || '400'} ${fontSize}px ${style?.fontFamily || 'Arial, sans-serif'}`;
        const key = `${font}|${style?.letterSpacing}|${text}`;
        if (!measured.has(key)) {
            if (context) context.font = font;
            const spacing = parseFloat(style?.letterSpacing) || 0;
            const width = context ? context.measureText(text).width : Array.from(text).length * fontSize * 0.6;
            measured.set(key, width + Math.max(0, Array.from(text).length - 1) * spacing);
        }
        let padding = gutter(cell);
        if (kind === 'header') {
            const sorted = Boolean(column.sortOrder);
            const filtered = hasHeaderFilter && column.allowHeaderFiltering !== false;
            // Match the header content gutters in ant.scss, including centered
            // headers where indicators reserve space on both sides.
            const indicators = !sorted && !filtered ? 0 : column.alignment === 'center' ? 34 : sorted && filtered ? 31 : 17;
            padding = gutter(headerCell) + indicators + (column.sortIndex > 0 ? 12 : 0);
        }
        return measured.get(key) + padding;
    };
};

export const getGridBestFitRows = (rows, remotePaging, pageIndex, pageSize) => {
    if (!Array.isArray(rows)) return [];
    if (remotePaging || !(pageSize > 0)) return rows;
    const start = Math.max(0, Number(pageIndex) || 0) * pageSize;
    return rows.slice(start, start + pageSize);
};

export const calculateGridBestFit = (columns, rows, { cache, measure, dataReady = true, getOption } = {}) => {
    const sizes = new Map();
    // Dòng nháp của lưới nhập trực tiếp (gần như rỗng) không phải dữ liệu: phiếu mới chỉ có dòng nháp vẫn coi là
    // "chưa có dữ liệu" → dùng độ rộng đã đo lần trước (cache), không đo theo dòng rỗng và không ghi đè cache.
    const sample = dataReady && Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object' && !isInlineDraftRow(row)) : [];
    const hasData = sample.length > 0;
    columns.filter(eligible).forEach((column) => {
        if (!hasData) {
            const cached = cache?.get(column);
            if (cached) { sizes.set(column, cached); return; }
            if (!measure) return;
            if (!column._antLookupTexts?.length) {
                // Chưa có dữ liệu (danh sách trống/đang nạp): rộng theo chữ tiêu đề, không theo mức
                // "ưa thích" của vai trò (tên 190px, văn bản dài 260px...) — mức đó làm adaptive
                // ẩn hàng loạt cột dù tiêu đề ngắn. Có dữ liệu thì đo lại theo nội dung.
                const headerColumn = getOption
                    ? { ...column, sortOrder: getOption(column, 'sortOrder'), sortIndex: getOption(column, 'sortIndex') }
                    : column;
                const width = Math.ceil(Math.max(column._antBestFit.minimum || 0, measure(column.caption || '', 'header', false, headerColumn)));
                sizes.set(column, { width, minWidth: width, contentWidth: width });
                return;
            }
        }
        const policy = column._antBestFit;
        const texts = new Map();
        const addText = (value, bold = false) => {
            if (value === undefined || value === null) return;
            const normalized = String(value).replace(/\s+/g, ' ').trim().slice(0, 1000);
            if (normalized) texts.set(normalized, Boolean(texts.get(normalized) || bold));
        };
        // Finite lookup options are a stronger source than the current page:
        // measuring only rows could narrow the column below another valid label.
        // ĐVT: đo theo dữ liệu đang hiện khi đã có dòng — danh mục ĐVT đầy đủ có thể chứa
        // tên dài làm cột phình ra dù trang hiện tại chỉ dùng "Cái", "Kg".
        if (!(hasData && policy.role === 'unit')) column._antLookupTexts?.forEach((text) => addText(text));
        const link = column.cellTemplate === 'renderLinkCell';
        const boldTemplate = column.cellTemplate === 'renderBoldCell';
        const displayColumn = link ? { ...column, calculateDisplayValue: column.calculateCellValue } : column;
        sample.forEach((row) => {
            if (column.cellTemplate !== 'renderCheckboxCell')
                addText(getGridDisplayValue({ data: row }, displayColumn, !link && !boldTemplate), boldTemplate && row.isBold);
        });
        let contentWidth = column.cellTemplate === 'renderCheckboxCell' ? 40 : 0;
        texts.forEach((bold, text) => { contentWidth = Math.max(contentWidth, measure(text, 'cell', bold, column)); });
        // Trạng thái sắp xếp hiện tại của DevExtreme (người dùng bấm header), không phải
        // sortOrder lúc khởi tạo cấu hình — cột vừa được sắp phải chừa chỗ cho icon.
        const headerColumn = getOption
            ? { ...column, sortOrder: getOption(column, 'sortOrder'), sortIndex: getOption(column, 'sortIndex') }
            : column;
        const headerWidth = Math.ceil(measure(column.caption || '', 'header', false, headerColumn));
        contentWidth = Math.ceil(Math.max(headerWidth, contentWidth, policy.minimum));
        const scalar = ['boolean', 'quantity', 'unit', 'money', 'date'].includes(policy.role) || column.dataType === 'number';
        const textMinimum = ['primaryText', 'name', 'longText', 'other'].includes(policy.role) ? 120 : 64;
        const minimum = Math.max(policy.minimum, Math.min(contentWidth,
            scalar ? 320 : Math.max(textMinimum, Math.min(240, headerWidth))));
        // This is only a bootstrap size without a viewport, never an upper limit
        // during allocation. Explicit minimums remain authoritative.
        const width = Math.max(minimum, Math.min(320, contentWidth));
        sizes.set(column, { width, minWidth: minimum, contentWidth });
    });
    // Column initialization runs during React render and passes a read-only cache.
    if (hasData) cache?.save?.(columns, sizes);
    return sizes;
};

// Call within AntGrid's existing beginUpdate/endUpdate transaction.
export const applyGridBestFit = (columns, sizes, instance) => {
    let changes = 0;
    columns.filter((column) => eligible(column) || flexible(column)).forEach((column) => {
        const size = sizes.get(column);
        if (!size) return;
        const key = column.name || column.dataField;
        const currentWidth = instance ? instance.columnOption(key, 'width') : column.width;
        const currentMin = instance ? instance.columnOption(key, 'minWidth') : column.minWidth;
        const differs = (a, b) => typeof a === 'number' && typeof b === 'number' ? Math.abs(a - b) >= 1 : a !== b;
        // Mức tối thiểu tự chia chỗ giữ riêng; DevExtreme chỉ nhận mức sàn khi kéo.
        column._antLayoutMinWidth = size.minWidth;
        const resizeMin = toResizeMinWidth(size.minWidth, size.width);
        if (!differs(currentWidth, size.width) && !differs(currentMin, resizeMin)) return;
        column.width = size.width;
        column.minWidth = resizeMin;
        if (instance) instance.columnOption(key, { width: size.width, minWidth: resizeMin });
        changes++;
    });
    if (instance && sizes.adaptiveHidden instanceof Set) changes += syncAdaptiveHiding(instance, columns, sizes.adaptiveHidden);
    if (changes && instance) scheduleGridDimensions(instance);
    return changes;
};

// Cho DevExtreme ẩn đúng các cột bố cục đã chọn (hidden): cột còn hiện mà mức ưu tiên thấp hơn cột bị ẩn (tức DevExtreme
// sẽ ẩn nó trước) → tạm bỏ hidingPriority; ngược lại trả mức gốc. Không có cột nào ẩn → trả hết mức gốc.
const syncAdaptiveHiding = (instance, columns, hidden) => {
    let saved = priorityOverrides.get(instance);
    if (!saved) {
        saved = new Map();
        priorityOverrides.set(instance, saved);
    }
    const option = (column, name) => instance.columnOption(columnId(column), name);
    let maxHidden = -Infinity;
    hidden.forEach((column) => {
        const priority = naturalHidingPriority(instance, column, option);
        if (typeof priority === 'number' && priority > maxHidden) maxHidden = priority;
    });
    let changes = 0;
    columns.forEach((column) => {
        const key = column && columnId(column);
        if (!key || column.isSystemCol || option(column, 'sortOrder')) return;
        const natural = naturalHidingPriority(instance, column, option);
        if (typeof natural !== 'number') return;
        const current = option(column, 'hidingPriority');
        if (!hidden.has(column) && natural < maxHidden) {
            if (!saved.has(key)) saved.set(key, natural);
            if (current !== undefined && current !== null) {
                instance.columnOption(key, 'hidingPriority', undefined);
                changes++;
            }
        } else if (saved.has(key)) {
            saved.delete(key);
            if (current !== natural) {
                instance.columnOption(key, 'hidingPriority', natural);
                changes++;
            }
        }
    });
    return changes;
};

// Cột độ rộng cố định (không best-fit/flexible) đang được sắp xếp: nếu chữ header + icon
// không vừa thì tạm nới cột để header không xuống dòng; bỏ sắp xếp thì trả độ rộng cũ.
const sortWidened = new WeakMap();
export const fitSortedHeaderWidths = (instance, columns, measure) => {
    if (!instance || !(columns instanceof Array) || !measure) return 0;
    let widened = sortWidened.get(instance);
    if (!widened) {
        widened = new Map();
        sortWidened.set(instance, widened);
    }
    let changes = 0;
    columns.forEach((column) => {
        if (!column || column.isSystemCol || eligible(column) || flexible(column)) return;
        const key = column.name || column.dataField;
        if (!key || !column.caption) return;
        const sortOrder = instance.columnOption(key, 'sortOrder');
        const width = instance.columnOption(key, 'width');
        const original = widened.get(key);
        if (sortOrder && instance.columnOption(key, 'visible') !== false) {
            const base = original !== undefined ? original : width;
            if (typeof base !== 'number') return;
            const needed = Math.ceil(measure(column.caption, 'header', false, { ...column, sortOrder, sortIndex: instance.columnOption(key, 'sortIndex') }));
            const target = Math.max(base, needed);
            if (target !== width) {
                if (original === undefined) widened.set(key, base);
                instance.columnOption(key, 'width', target);
                changes++;
            }
        } else if (original !== undefined) {
            widened.delete(key);
            if (width !== original) {
                instance.columnOption(key, 'width', original);
                changes++;
            }
        }
    });
    return changes;
};

export const invalidateGridBestFit = (instance) => {
    const state = fitStates.get(instance);
    if (state && !state.applying && !state.reconciling) state.dataDirty = true;
};

// Người dùng yêu cầu tự căn lại (nhấp đúp vạch ngăn cột): đo lại theo nội dung trang hiện tại, bỏ qua chốt bố cục.
export const refitGridBestFit = (instance, columns, cache) => {
    if (!instance) return;
    const state = getFitState(instance);
    state.dataDirty = true;
    state.forceMeasure = true;
    state.layoutKey = null;
    state.hold = null;
    scheduleGridBestFit(instance, columns, cache);
};

// Người dùng thu hẹp 1 cột khi lưới đang có cột tự ẩn: KHÔNG chia phần trống cho cột khác; đến khi đủ chỗ cho cột
// tự ẩn thì bố cục chạy lại và cột đó hiện ra. Bỏ giữ khi khung/cột/dữ liệu đổi hoặc khi tự căn.
// sticky: đang kéo — giữ cả khi khoá bố cục đổi theo từng bước kéo (thả chuột thì gọi lại không sticky).
export const holdGridLayoutUntilReveal = (instance, { sticky = false } = {}) => {
    if (!instance) return;
    getFitState(instance).hold = { key: null, sticky };
};

export const hasAdaptiveHiddenColumns = (instance) =>
    Boolean(instance?.getVisibleColumns?.().some((column) => column.visibleWidth === 'adaptiveHidden'));

// Chốt độ rộng theo dữ liệu (vd đang lọc trên cột): dữ liệu mới / đổi trang / đổi sắp xếp KHÔNG đo lại cột; khung đổi
// kích thước vẫn chia lại chỗ trên số đo cũ. Mở chốt → đo lại theo trang hiện tại.
export const setGridBestFitFrozen = (instance, frozen) => {
    if (!instance) return;
    const state = getFitState(instance);
    state.frozen = Boolean(frozen);
    if (!frozen) state.dataDirty = true;
};

// React may rebuild column definitions while keeping the same grid instance.
// Preserve the last published layout until its replacement can be committed,
// instead of briefly reverting to bootstrap/cache widths on every parent render.
export const getCommittedGridWidths = (instance, columns) => {
    const committed = fitStates.get(instance)?.committed;
    const sizes = new Map();
    if (committed) columns.filter(eligible).forEach((column) => {
        const size = committed.get(columnKey(column));
        if (size) sizes.set(column, size);
    });
    return sizes;
};

const getFitState = (instance) => {
    let state = fitStates.get(instance);
    if (state) return state;
    state = { dataDirty: true, scrollbar: 0 };
    fitStates.set(instance, state);
    const dispose = () => {
        state.disposed = true;
        state.cancelFrame?.();
        state.dataSource?.off?.('changed', state.onDataChanged);
        state.fonts?.removeEventListener?.('loadingdone', state.onFontsLoaded);
        fitStates.delete(instance);
    };
    state.onDataChanged = () => invalidateGridBestFit(instance);
    state.onFontsLoaded = () => {
        if (state.disposed) return;
        state.measure = undefined;
        state.dataDirty = true;
        scheduleGridBestFit(instance, state.columns, state.cache);
    };
    instance.on?.('disposing', dispose);
    return state;
};

// One writer, after native data/React templates have completed their current
// transaction. Repeated contentReady events coalesce into the same frame.
export const scheduleGridBestFit = (instance, columns, cache) => {
    if (!instance) return;
    if (!columns?.some((column) => eligible(column) || flexible(column))) {
        const previous = fitStates.get(instance);
        if (previous) {
            previous.cancelFrame?.();
            previous.cancelFrame = null;
            previous.columns = columns || [];
            previous.intrinsic = null;
            previous.committed = null;
            previous.dataDirty = true;
        }
        return;
    }
    const state = getFitState(instance);
    state.next = { columns, cache };
    // A native repaint may reveal a scrollbar/command after our transaction.
    // Allow one deferred check; unchanged inputs return before measuring/writing.
    if (state.cancelFrame) return;
    const root = instance.element();
    const element = root?.get ? root.get(0) : root;
    const view = element?.ownerDocument?.defaultView;
    const run = () => {
        state.cancelFrame = null;
        if (!state.disposed && element?.isConnected && element.clientWidth > 0)
            fitVisibleGridPage(instance, state.next.columns, state.next.cache);
    };
    if (view?.requestAnimationFrame) {
        const frame = view.requestAnimationFrame(run);
        state.cancelFrame = () => view.cancelAnimationFrame(frame);
    } else {
        const timer = setTimeout(run, 0);
        state.cancelFrame = () => clearTimeout(timer);
    }
};

// contentReady is a notification that rendering finished, not a new data page.
// Keep intrinsic sizes for the page and reallocate them only on external layout
// changes. Width changes/updateDimensions must never become new measurement input.
export const fitVisibleGridPage = (instance, columns, cache) => {
    if (!instance || !columns.some((column) => eligible(column) || flexible(column))) return 0;
    const state = getFitState(instance);
    if (state.applying || state.reconciling || state.disposed) return 0;
    const dataSource = instance.getDataSource();
    if (state.dataSource !== dataSource) {
        state.dataSource?.off?.('changed', state.onDataChanged);
        state.dataSource = dataSource;
        dataSource?.on?.('changed', state.onDataChanged);
        state.dataDirty = true;
    }
    if (state.columns !== columns) { state.columns = columns; state.dataDirty = true; }
    state.cache = cache;
    // Keep the last committed layout while a new page is in flight. Applying
    // cached/old rows here would visibly undo the incoming page's layout.
    if (dataSource?.isLoading()) return 0;
    const root = instance.element();
    const element = root?.get ? root.get(0) : root;
    const outerWidth = getGridContentWidth(element);
    const viewport = getGridViewportWidth(element);
    // A scrollbar can appear/disappear as text wraps. Reserve the largest gutter
    // observed at this outer width, rather than alternating the budget forever.
    const gutter = Math.max(0, Math.min(40, outerWidth - viewport));
    state.scrollbar = state.outerWidth === outerWidth ? Math.max(state.scrollbar, gutter) : gutter;
    state.outerWidth = outerWidth;
    const width = Math.max(0, outerWidth - state.scrollbar);
    const option = (column, name) => instance.columnOption?.(column.name || column.dataField, name) ?? column[name];
    const pageKey = JSON.stringify([dataSource?.pageIndex?.(), dataSource?.pageSize?.()]);
    if (state.pageKey !== pageKey) { state.pageKey = pageKey; state.dataDirty = true; }
    // Đổi cột sắp xếp: đo lại chữ header (icon sắp xếp chiếm thêm chỗ).
    const sortKey = JSON.stringify(columns.map((column) => [option(column, 'sortOrder'), option(column, 'sortIndex')]));
    if (state.sortKey !== sortKey) { state.sortKey = sortKey; state.dataDirty = true; }
    if (state.frozen && state.intrinsic && !state.forceMeasure) state.dataDirty = false;
    const layoutKey = JSON.stringify([width, columns.map((column) => [column.name || column.dataField,
        column.visible, option(column, 'visible'), option(column, 'groupIndex'), option(column, 'showWhenGrouped'),
        option(column, 'sortOrder'), option(column, 'sortIndex'), column._antUserMinWidth || 0, option(column, 'hidingPriority'),
        !eligible(column) && !flexible(column) ? [option(column, 'width'), option(column, 'minWidth')] : null]),
        (instance.getVisibleColumns?.() || []).filter((column) => column.command && column.command !== 'adaptive')
            .map((column) => [column.name, column.command, column.width, column.minWidth])]);
    // Đang giữ bố cục (người dùng vừa thu hẹp cột khi có cột tự ẩn): thứ khác đổi (khung, cột, dữ liệu) → bỏ giữ.
    if (state.hold && !state.hold.sticky && (state.dataDirty || (state.hold.key && state.hold.key !== layoutKey))) state.hold = null;
    if (!state.dataDirty && state.layoutKey === layoutKey) return 0;
    if (state.dataDirty || !state.intrinsic) {
        // Use the loaded page, never the fraction exposed by virtual row DOM.
        const page = dataSource?.group?.()?.length ? null : dataSource?.items?.();
        const rows = Array.isArray(page) && page.length ? page :
            instance.getVisibleRows().filter((row) => row.rowType === 'data').map((row) => row.data);
        // Luôn đo (kể cả danh sách trống) để độ rộng bám chữ tiêu đề thay vì mức mặc định của vai trò.
        const needsMeasurement = columns.some(eligible);
        if (needsMeasurement && !state.measure) state.measure = createGridTextMeasurer(element);
        if (!state.fonts && element?.ownerDocument?.fonts) {
            state.fonts = element.ownerDocument.fonts;
            state.fonts.addEventListener?.('loadingdone', state.onFontsLoaded);
        }
        state.intrinsic = calculateGridBestFit(columns, rows, { cache, measure: state.measure, getOption: option });
        state.dataDirty = false;
        state.forceMeasure = false;
    }
    state.layoutKey = layoutKey;
    const sizes = calculateGridFlexibleWidths(columns, state.intrinsic, { width, instance });
    if (state.hold) {
        // Chưa hiện thêm được cột tự ẩn nào → giữ nguyên độ rộng các cột khác (phần trống để trống), đủ chỗ mới chia lại.
        const hiddenNow = (instance.getVisibleColumns?.() || []).filter((column) => column.visibleWidth === 'adaptiveHidden').length;
        if (state.hold.sticky || !(sizes.adaptiveHidden instanceof Set) || sizes.adaptiveHidden.size >= hiddenNow) {
            state.hold.key = layoutKey;
            return 0;
        }
        state.hold = null;
    }
    state.committed = new Map(columns.filter(eligible).filter((column) => sizes.has(column))
        .map((column) => [columnKey(column), sizes.get(column)]));
    state.applying = true;
    instance.beginUpdate();
    try { return applyGridBestFit(columns, sizes, instance); }
    finally {
        try { instance.endUpdate(); }
        finally { state.applying = false; }
    }
};
